import type { UserRole } from "../api/types";

export interface SimpleBillingRouteDefinition {
  path: string;
  label: string;
  title: string;
  description: string;
  roles: UserRole[];
}

const operationalRoles: UserRole[] = ["owner", "manager", "staff"];
const managementRoles: UserRole[] = ["owner", "manager"];
const readRoles: UserRole[] = ["owner", "manager", "staff", "super_admin"];
const draftEditRoute: SimpleBillingRouteDefinition = {
  path: "/simple-billing/invoices/:invoiceId/edit",
  label: "Edit Draft",
  title: "Edit Bill Draft",
  description: "Edit an existing Simple Billing draft.",
  roles: operationalRoles,
};
const billDetailRoute: SimpleBillingRouteDefinition = {
  path: "/simple-billing/invoices/:invoiceId",
  label: "Bill Details",
  title: "Bill Details",
  description: "View a saved bill.",
  roles: readRoles,
};

export const SIMPLE_BILLING_ROUTES: SimpleBillingRouteDefinition[] = [
  {
    path: "/simple-billing/new",
    label: "New Bill",
    title: "New Bill",
    description: "Create, save, edit, and finalize Simple Billing drafts.",
    roles: operationalRoles,
  },
  {
    path: "/simple-billing/estimates",
    label: "Wood Estimates",
    title: "Comparative Wood Estimates",
    description: "Compare one rectangular timber requirement across different woods and rates.",
    roles: readRoles,
  },
  {
    path: "/simple-billing/invoices",
    label: "Previous Bills",
    title: "Previous Bills",
    description: "Find saved drafts and finalized bills.",
    roles: readRoles,
  },
  {
    path: "/simple-billing/pending-payments",
    label: "Pending Payments",
    title: "Pending Payments",
    description: "Track unpaid, partially paid, and overdue customer bills.",
    roles: readRoles,
  },
  {
    path: "/simple-billing/collections",
    label: "Collections Summary",
    title: "Collections Summary",
    description: "Review customer payments and reversals by collection date.",
    roles: readRoles,
  },
  {
    path: "/simple-billing/expenses",
    label: "Expenses",
    title: "Expenses",
    description: "Record business spending and review expenses by expense date.",
    roles: ["owner", "manager", "super_admin"],
  },
  {
    path: "/simple-billing/business-summary",
    label: "Business Summary",
    title: "Business Summary",
    description: "Review sales, customer collections, and recorded expenses separately.",
    roles: ["owner", "manager", "super_admin"],
  },
  {
    path: "/simple-billing/products",
    label: "Products & Rates",
    title: "Products & Rates",
    description: "Manage products, units, and default billing rates.",
    roles: managementRoles,
  },
  {
    path: "/simple-billing/inventory",
    label: "Inventory",
    title: "Inventory",
    description: "Receive stock, review availability, and audit stock movements.",
    roles: readRoles,
  },
  {
    path: "/simple-billing/reports",
    label: "Sales Summary",
    title: "Sales Summary",
    description: "Review finalized and cancelled bill totals for a selected period.",
    roles: readRoles,
  },
  {
    path: "/simple-billing/settings",
    label: "Billing Settings",
    title: "Billing Settings",
    description: "Manage seller details, bill numbering, and print defaults.",
    roles: managementRoles,
  },
];

export function simpleBillingNavigationForRole(role: UserRole): SimpleBillingRouteDefinition[] {
  return SIMPLE_BILLING_ROUTES.filter((route) => route.roles.includes(role));
}

export function simpleBillingRoute(pathname: string): SimpleBillingRouteDefinition | undefined {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const direct = SIMPLE_BILLING_ROUTES.find((route) => route.path === normalized);
  if (direct) return direct;
  if (/^\/simple-billing\/invoices\/[^/]+\/edit$/.test(normalized)) return draftEditRoute;
  return /^\/simple-billing\/invoices\/[^/]+$/.test(normalized) ? billDetailRoute : undefined;
}
